import {
  keepPreviousData,
  QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useCallback } from 'react';
import { PAGINATION_PAGE_SIZE_LARGE } from '~/constants';

import { queryKeysAnalytics } from '../queryKeys';

import { fetchTopSitesBreakdown } from './fetchTopSitesBreakdown';
import {
  TopSitesBreakdownQueryParams,
  TopSitesBreakdownResponse,
} from './types';
import { zodTopSitesBreakdownQueryParamsSchema } from './validations';

/**
 * Generates the query options for the top sites breakdown query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching the top sites breakdown.
 * @param options - Additional options for the query.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 *   Use this for prefetching in route loaders to prevent request cancellation when the loader completes.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getTopSitesBreakdownQueryOptions = ({
  queryParams,
  options,
  ignoreSignal = false,
}: {
  queryParams: TopSitesBreakdownQueryParams;
  options?: Omit<
    UseQueryOptions<
      TopSitesBreakdownResponse,
      Error,
      TopSitesBreakdownResponse
    >,
    'queryKey' | 'queryFn'
  >;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodTopSitesBreakdownQueryParamsSchema.parse(queryParams);

  const isEnabled = Boolean(
    validatedQueryParams.dateRange.dateFrom &&
    validatedQueryParams.dateRange.dateTo,
  );

  return {
    enabled: isEnabled,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchTopSitesBreakdown(validatedQueryParams, {
        signal: ignoreSignal ? undefined : signal,
      }),
    queryKey: queryKeysAnalytics.getTopSitesBreakdown(validatedQueryParams),
    staleTime: ms('5m'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the top sites and their signature rates data with given query parameters.
 *
 * @param queryParams - The query parameters for fetching the top sites by signature rate.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/get_top_sites_breakdown_site_statistics_top_sites_post
 */
export const useQueryTopSitesBreakdown = (
  queryParams: TopSitesBreakdownQueryParams,
  options?: Omit<
    UseQueryOptions<
      TopSitesBreakdownResponse,
      Error,
      TopSitesBreakdownResponse
    >,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getTopSitesBreakdownQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });

/**
 * Generates the infinite query options for prefetching the first page of top sites breakdown.
 *
 * @param queryParams - The query parameters for fetching the top sites breakdown.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 * @returns The infinite query options for prefetching.
 */
export const getTopSitesBreakdownInfiniteQueryOptions = ({
  queryParams,
  ignoreSignal = false,
}: {
  queryParams: Partial<TopSitesBreakdownQueryParams>;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodTopSitesBreakdownQueryParamsSchema.parse(queryParams);

  return {
    getNextPageParam: (lastPage: TopSitesBreakdownResponse) => {
      const nextOffset = (lastPage.offset ?? 0) + (lastPage.data?.length ?? 0);

      return nextOffset < (lastPage.totalCount ?? 0) ? nextOffset : undefined;
    },
    initialPageParam: 0,
    queryFn: async ({
      pageParam: pageParameter = 0,
      signal,
    }: QueryFunctionContext) =>
      fetchTopSitesBreakdown(
        {
          ...validatedQueryParams,
          offset: pageParameter,
        },
        { signal: ignoreSignal ? undefined : signal },
      ),
    queryKey:
      queryKeysAnalytics.getTopSitesBreakdownInfinite(validatedQueryParams),
    staleTime: ms('5m'),
  };
};

/**
 * React Query based custom hook for windowed/sparse data fetching of sites by signature rate.
 * Unlike useInfiniteQuery, this approach only fetches pages for currently visible items,
 * allowing users to jump to any position without loading all intermediate data.
 *
 * @param queryParams - The query parameters for fetching sites by signature rate.
 * @returns An object containing:
 *   - fetchPage: Function to fetch a specific page by offset
 *   - queryKey: The base query key for cache management
 *   - pageSize: The page size being used
 */
export const useQueryTopSitesBreakdownWindowed = (
  queryParams: Partial<TopSitesBreakdownQueryParams>,
) => {
  const validatedQueryParams =
    zodTopSitesBreakdownQueryParamsSchema.parse(queryParams);

  const pageSize = validatedQueryParams.limit ?? PAGINATION_PAGE_SIZE_LARGE;

  // Stable fetchPage function for the windowed component
  const fetchPage = useCallback(
    async (offset: number, signal: AbortSignal) => {
      return fetchTopSitesBreakdown(
        { ...validatedQueryParams, offset },
        { signal },
      );
    },
    [
      validatedQueryParams.dateRange.dateFrom,
      validatedQueryParams.dateRange.dateTo,
      validatedQueryParams.sortBy,
      validatedQueryParams.sortOrder,
      validatedQueryParams.signatureFilter,
      validatedQueryParams.limit,
    ],
  );

  return {
    fetchPage,
    pageSize,
    queryKey: queryKeysAnalytics.getTopSitesBreakdown(validatedQueryParams),
  };
};
